import {
  PromptTemplate,
  PromptTemplateCreationParams,
  PromptTemplateResponse,
  PromptTemplateUpdateParams,
  SinglePromptTemplateResponse
} from "../../types/prompt-template";
import { logError } from "../../utils/error";
import { supabase } from "../supabase/client";

/**
 * Fetch all prompt templates
 * @param enabledOnly - If true, only returns enabled templates
 */
export const fetchPromptTemplates = async (enabledOnly = false): Promise<PromptTemplateResponse> => {
  try {
    let query = supabase.from("prompt_template").select("*");

    if (enabledOnly) {
      query = query.eq("enabled", true);
    }

    query = query.order("modified", { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return {
      data: data as unknown as PromptTemplate[],
      error: null
    };
  } catch (error) {
    logError("Error fetching prompt templates", error);
    return {
      data: null,
      error: error as Error
    };
  }
};

/**
 * Fetch a single prompt template by ID
 * @param id - The UUID of the template to fetch
 */
export const fetchPromptTemplateById = async (id: string): Promise<SinglePromptTemplateResponse> => {
  try {
    const { data, error } = await supabase
      .from("prompt_template")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      throw error;
    }

    return {
      data: data as unknown as PromptTemplate,
      error: null
    };
  } catch (error) {
    logError(`Error fetching prompt template with ID ${id}`, error);
    return {
      data: null,
      error: error as Error
    };
  }
};

/**
 * Create a new prompt template
 * @param template - The template data to create
 */
export const createPromptTemplate = async (
  template: PromptTemplateCreationParams
): Promise<SinglePromptTemplateResponse> => {
  try {
    const { data, error } = await supabase
      .from("prompt_template")
      // NOTE(types): app-level union fields (category/audience/about/primaryFunction/scope) and `metadata` shape are narrower than the generated Insert type.
      .insert([template] as never)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data: data as unknown as PromptTemplate,
      error: null
    };
  } catch (error) {
    logError("Error creating prompt template", error);
    return {
      data: null,
      error: error as Error
    };
  }
};

/**
 * Update an existing prompt template
 * @param id - The UUID of the template to update
 * @param updates - The template data to update
 */
export const updatePromptTemplate = async (
  id: string,
  updates: PromptTemplateUpdateParams
): Promise<SinglePromptTemplateResponse> => {
  try {
    const updatedData = {
      ...updates,
      modified: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from("prompt_template")
      // NOTE(types): same narrowing mismatch as `createPromptTemplate` — app unions vs generated Update type.
      .update(updatedData as never)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data: data as unknown as PromptTemplate,
      error: null
    };
  } catch (error) {
    logError(`Error updating prompt template with ID ${id}`, error);
    return {
      data: null,
      error: error as Error
    };
  }
};

/**
 * Delete a prompt template
 * @param id - The UUID of the template to delete
 */
export const deletePromptTemplate = async (id: string): Promise<{ success: boolean; error: Error | null }> => {
  try {
    const { error } = await supabase
      .from("prompt_template")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }

    return {
      success: true,
      error: null
    };
  } catch (error) {
    logError(`Error deleting prompt template with ID ${id}`, error);
    return {
      success: false,
      error: error as Error
    };
  }
};

/**
 * Toggle the enabled status of a prompt template
 * @param id - The UUID of the template
 * @param enabled - The new enabled status
 */
export const togglePromptTemplateStatus = async (
  id: string,
  enabled: boolean
): Promise<SinglePromptTemplateResponse> => {
  try {
    const { data, error } = await supabase
      .from("prompt_template")
      .update({
        enabled,
        modified: new Date().toISOString()
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data: data as unknown as PromptTemplate,
      error: null
    };
  } catch (error) {
    logError(`Error toggling status for prompt template with ID ${id}`, error);
    return {
      data: null,
      error: error as Error
    };
  }
};
